/**
 * Trade-map share links (Asana 1218807104530338).
 *
 * Every channel lands on the sharer's PUBLIC PROFILE with the Trade History tab
 * open, tagged with UTMs and the sharer's username under TRADE_MAP_REFERRAL_PARAM.
 *
 * The param is `via`, NOT `ref` (Eric's ruling, 2026-09-23). Two existing
 * readers consume `?ref` on every landing: routes/index.tsx writes any ref into
 * the 30-day tm_ref AFFILIATE cookie (last touch, overwriting), and
 * utils/referralSource.ts gives ref precedence over utm_source, so SOURCE would
 * read the sharer's username instead of trade_map_share. Affiliate credit is
 * money owed to partners and a social share must never overwrite it. `via` is
 * captured by utils/attribution.ts into the tm_attribution entry alongside the
 * UTMs and reaches the backend as attribution.referredByUsername at signup.
 * Nothing else reads it. Change the param name here and nowhere else.
 */

export const TRADE_MAP_REFERRAL_PARAM = 'via';

export const TRADE_MAP_UTM_SOURCE = 'trade_map_share';

/** The four link channels. copy_image and download share the analytics event, not a link. */
export type TradeMapShareChannel = 'x' | 'bluesky' | 'reddit' | 'copy_link';

/** Every share ACTION the modal reports to analytics */
export type TradeMapShareAction = TradeMapShareChannel | 'copy_image' | 'download';

/** The GA4 event name for every trade-map share action */
export const TRADE_MAP_SHARE_EVENT = 'trade_map_share';

/**
 * The backend's `code` on its 429 when the map PNG was uploaded inside the
 * cooldown (one upload per 10 s per user). Listed in utils/inlineHandled429 so
 * the global 429 handler neither toasts nor pauses polling for it: the modal
 * treats that 429 as a no-op (ruling g). One definition here, one in the
 * backend (TradeMapImageService). The backend's generic limiters never send a
 * code, so their 429s still pause polling.
 */
export const TRADE_MAP_IMAGE_COOLDOWN_CODE = 'TRADE_MAP_IMAGE_COOLDOWN';

/** The backend cooldown between two uploads by one user, mirrored client-side as a courtesy */
export const TRADE_MAP_UPLOAD_COOLDOWN_MS = 10_000;

// Client-side courtesy skip: a reopen inside the cooldown sends no request at
// all. Keyed on the LAST SUCCESSFUL upload only (a 400 or 429 starts nothing)
// and per viewer, so a different account in the same tab is not held back.
// The backend cooldown stays the enforcement; a client that ignores this gets
// the same 429.
let lastUpload: { userId: number; at: number } | null = null;

export const shouldUploadTradeMap = (userId: number, now: number = Date.now()): boolean =>
  !lastUpload || lastUpload.userId !== userId || now - lastUpload.at >= TRADE_MAP_UPLOAD_COOLDOWN_MS;

export const recordTradeMapUpload = (userId: number, now: number = Date.now()): void => {
  lastUpload = { userId, at: now };
};

/** Tests only */
export const resetTradeMapUploadMemory = (): void => {
  lastUpload = null;
};

const SITE_ORIGIN = 'https://trademagic.gg';

/**
 * The profile canonical is lowercase and percent-encoded (SEOHelmet does the
 * same), so the link matches the canonical the crawler meta emits. The `via`
 * value keeps the username's own case for readability; the backend lowercases
 * it on write.
 */
export const tradeMapShareUrl = (username: string, channel: TradeMapShareChannel): string => {
  const params = new URLSearchParams({
    tab: 'history',
    utm_source: TRADE_MAP_UTM_SOURCE,
    utm_medium: 'social',
    utm_campaign: channel,
    [TRADE_MAP_REFERRAL_PARAM]: username,
  });
  return `${SITE_ORIGIN}/profile/u/${encodeURIComponent(username.toLowerCase())}?${params.toString()}`;
};
